import React, { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { useUser } from 'src/lib/contexts/UserContext'
import useNavigation from 'src/lib/hooks/useNavigation'
import { CaretDown } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from '@shopflo/ui'
import ProfileOverlay from './overlay/ProfileOverlay'
import NewsWidgetV2 from './NewsWidgetV2'
import { useCheckoutUI } from 'src/lib/contexts/CheckoutUIContext'
import LogoWithFallback from 'src/components/common/LogoWithFallback'
import { classNames } from 'src/lib/utils/helpers'

const NavigationHeader = () => {
  const { user, isUserProfileLoading } = useUser()
  const [open, setOpen] = useState(false)
  const {
    getters: { sidebarOpen }
  } = useNavigation()

  const {
    getters: { checkoutUI }
  } = useCheckoutUI()

  const logoUrl = checkoutUI?.logoUrl ?? ''

  const getLoggedInOrg = useMemo(() => {
    if (!Boolean(user)) return ''
    return user?.organisations?.find((org: any) => org?.logged_in)?.name
  }, [user])

  const orgFirstLetter = getLoggedInOrg?.charAt(0)?.toUpperCase()
  const [avatarType, setAvatarType] = useState<'image' | 'text'>('text')

  return (
    <motion.div
      className={classNames(
        'flex justify-start pr-2 py-[10px] w-full min-h-12',
        sidebarOpen ? 'pl-3.5' : '',
        avatarType === 'image' ? 'pl-2 pr-2' : 'pl-3.5 pr-3.5'
      )}
      id='navigation-header'
    >
      <div className={classNames('flex justify-center items-center gap-2', sidebarOpen ? 'w-full' : 'w-fit')}>
        <motion.div
          key='sidebar-content'
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: '100%' }}
          exit={{ opacity: 0, width: 0 }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
          className={classNames(
            'text-text-sm-semibold text-zinc-800  flex items-center  gap-1 w-full',
            !isUserProfileLoading ? 'justify-start' : ''
          )}
        >
          <Popover open={open} onOpenChange={open => setOpen(open)}>
            <PopoverTrigger asChild>
              <div className='flex items-center gap-2 cursor-pointer' id='navigation-header-trigger'>
                <LogoWithFallback
                  logoUrl={logoUrl}
                  fallbackText={orgFirstLetter || ''}
                  id='navigation-header-logo'
                  expanded={sidebarOpen}
                  onTypeChange={setAvatarType}
                />
                {sidebarOpen &&
                  (isUserProfileLoading ? (
                    <div className='w-full h-3 min-w-38 bg-zinc-200 rounded-md animate-pulse' />
                  ) : (
                    <div className='flex items-center gap-1 pr-2 min-w-0'>
                      <p className='truncate max-w-36'>{getLoggedInOrg}</p>
                      <CaretDown size={12} className='text-zinc-500 flex-shrink-0' weight='fill' />
                    </div>
                  ))}
              </div>
            </PopoverTrigger>

            <PopoverContent
              sideOffset={8}
              align='start'
              className='bg-white w-64 p-0 !border-none !rounded-xl overflow-hidden'
              asChild
            >
              <div>
                <ProfileOverlay closePopover={() => setOpen(false)} />
              </div>
            </PopoverContent>
          </Popover>
          {sidebarOpen && !isUserProfileLoading && <NewsWidgetV2 />}
        </motion.div>
      </div>
    </motion.div>
  )
}

export default NavigationHeader
